/** SVG path geometry: sampling, length, morphing. */
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { lerp, radToDeg } from './index';
/** Flatten an SVG path `d` into polylines (M/L/H/V/C/Q/Z subset). */
export function parsePathSegments(d) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    var segments = [];
    var current = [];
    var cx = 0;
    var cy = 0;
    var sx = 0;
    var sy = 0;
    var tokens = (_a = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e[-+]?\d+)?/g)) !== null && _a !== void 0 ? _a : [];
    var i = 0;
    var readNum = function () { return parseFloat(tokens[i++]); };
    while (i < tokens.length) {
        var cmd = tokens[i++];
        var rel = cmd === cmd.toLowerCase();
        var c = cmd.toUpperCase();
        if (c === 'M') {
            if (current.length > 1)
                segments.push(current);
            cx = readNum();
            cy = readNum();
            sx = cx;
            sy = cy;
            current = [{ x: cx, y: cy }];
        }
        else if (c === 'L') {
            var px = readNum();
            var py = readNum();
            cx = rel ? ((_c = (_b = current[current.length - 1]) === null || _b === void 0 ? void 0 : _b.x) !== null && _c !== void 0 ? _c : 0) + px : px;
            cy = rel ? ((_e = (_d = current[current.length - 1]) === null || _d === void 0 ? void 0 : _d.y) !== null && _e !== void 0 ? _e : 0) + py : py;
            current.push({ x: cx, y: cy });
        }
        else if (c === 'H') {
            var px = readNum();
            cx = rel ? ((_g = (_f = current[current.length - 1]) === null || _f === void 0 ? void 0 : _f.x) !== null && _g !== void 0 ? _g : 0) + px : px;
            cy = (_j = (_h = current[current.length - 1]) === null || _h === void 0 ? void 0 : _h.y) !== null && _j !== void 0 ? _j : cy;
            current.push({ x: cx, y: cy });
        }
        else if (c === 'V') {
            var py = readNum();
            cy = rel ? ((_l = (_k = current[current.length - 1]) === null || _k === void 0 ? void 0 : _k.y) !== null && _l !== void 0 ? _l : 0) + py : py;
            cx = (_o = (_m = current[current.length - 1]) === null || _m === void 0 ? void 0 : _m.x) !== null && _o !== void 0 ? _o : cx;
            current.push({ x: cx, y: cy });
        }
        else if (c === 'Z') {
            if (current.length > 0) {
                current.push({ x: sx, y: sy });
                segments.push(current);
                current = [];
            }
            cx = sx;
            cy = sy;
        }
        else if (c === 'C') {
            var x1 = readNum();
            var y1 = readNum();
            var x2 = readNum();
            var y2 = readNum();
            var x = readNum();
            var y = readNum();
            var p0 = (_p = current[current.length - 1]) !== null && _p !== void 0 ? _p : { x: cx, y: cy };
            if (rel) {
                flattenCubic(p0, { x: p0.x + x1, y: p0.y + y1 }, { x: p0.x + x2, y: p0.y + y2 }, { x: p0.x + x, y: p0.y + y }, current);
                cx = p0.x + x;
                cy = p0.y + y;
            }
            else {
                flattenCubic(p0, { x: x1, y: y1 }, { x: x2, y: y2 }, { x: x, y: y }, current);
                cx = x;
                cy = y;
            }
        }
        else if (c === 'Q') {
            var x1 = readNum();
            var y1 = readNum();
            var x = readNum();
            var y = readNum();
            var p0 = (_q = current[current.length - 1]) !== null && _q !== void 0 ? _q : { x: cx, y: cy };
            if (rel) {
                flattenQuadratic(p0, { x: p0.x + x1, y: p0.y + y1 }, { x: p0.x + x, y: p0.y + y }, current);
                cx = p0.x + x;
                cy = p0.y + y;
            }
            else {
                flattenQuadratic(p0, { x: x1, y: y1 }, { x: x, y: y }, current);
                cx = x;
                cy = y;
            }
        }
    }
    if (current.length > 1)
        segments.push(current);
    return segments;
}
function flattenCubic(p0, p1, p2, p3, out, steps) {
    if (steps === void 0) { steps = 8; }
    for (var t = 1; t <= steps; t++) {
        var u = t / steps;
        var u2 = u * u;
        var u3 = u2 * u;
        var mt = 1 - u;
        var mt2 = mt * mt;
        var mt3 = mt2 * mt;
        out.push({
            x: mt3 * p0.x + 3 * mt2 * u * p1.x + 3 * mt * u2 * p2.x + u3 * p3.x,
            y: mt3 * p0.y + 3 * mt2 * u * p1.y + 3 * mt * u2 * p2.y + u3 * p3.y,
        });
    }
}
function flattenQuadratic(p0, p1, p2, out, steps) {
    if (steps === void 0) { steps = 6; }
    for (var t = 1; t <= steps; t++) {
        var u = t / steps;
        var mt = 1 - u;
        out.push({
            x: mt * mt * p0.x + 2 * mt * u * p1.x + u * u * p2.x,
            y: mt * mt * p0.y + 2 * mt * u * p1.y + u * u * p2.y,
        });
    }
}
function flattenSegments(segments) {
    var e_1, _a, e_2, _b;
    var pts = [];
    try {
        for (var segments_1 = __values(segments), segments_1_1 = segments_1.next(); !segments_1_1.done; segments_1_1 = segments_1.next()) {
            var seg = segments_1_1.value;
            try {
                for (var seg_1 = (e_2 = void 0, __values(seg)), seg_1_1 = seg_1.next(); !seg_1_1.done; seg_1_1 = seg_1.next()) {
                    var p = seg_1_1.value;
                    pts.push(p);
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (seg_1_1 && !seg_1_1.done && (_b = seg_1.return)) _b.call(seg_1);
                }
                finally { if (e_2) throw e_2.error; }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (segments_1_1 && !segments_1_1.done && (_a = segments_1.return)) _a.call(segments_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return pts;
}
function segmentLength(a, b) {
    return Math.hypot(b.x - a.x, b.y - a.y);
}
/** Total polyline length of a path. */
export function getPathLength(d) {
    var e_3, _a;
    var segments = parsePathSegments(d);
    var len = 0;
    try {
        for (var segments_2 = __values(segments), segments_2_1 = segments_2.next(); !segments_2_1.done; segments_2_1 = segments_2.next()) {
            var seg = segments_2_1.value;
            for (var i = 0; i < seg.length - 1; i++) {
                len += segmentLength(seg[i], seg[i + 1]);
            }
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (segments_2_1 && !segments_2_1.done && (_a = segments_2.return)) _a.call(segments_2);
        }
        finally { if (e_3) throw e_3.error; }
    }
    return len;
}
/** Point and tangent angle (degrees) at distance along path. */
export function getPointAtLength(d, distance) {
    var e_4, _a;
    var _b, _c;
    var segments = parsePathSegments(d);
    if (segments.length === 0 || segments.every(function (s) { return s.length === 0; })) {
        return { x: 0, y: 0, angle: 0 };
    }
    var remaining = Math.max(0, distance);
    var prev = (_b = segments[0][0]) !== null && _b !== void 0 ? _b : { x: 0, y: 0 };
    try {
        for (var segments_3 = __values(segments), segments_3_1 = segments_3.next(); !segments_3_1.done; segments_3_1 = segments_3.next()) {
            var seg = segments_3_1.value;
            for (var i = 0; i < seg.length - 1; i++) {
                var a = seg[i];
                var b = seg[i + 1];
                var len = segmentLength(a, b);
                if (len === 0)
                    continue;
                if (remaining <= len) {
                    var t = remaining / len;
                    var x = lerp(a.x, b.x, t);
                    var y = lerp(a.y, b.y, t);
                    var angle_1 = radToDeg(Math.atan2(b.y - a.y, b.x - a.x));
                    return { x: x, y: y, angle: angle_1 };
                }
                remaining -= len;
                prev = b;
            }
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (segments_3_1 && !segments_3_1.done && (_a = segments_3.return)) _a.call(segments_3);
        }
        finally { if (e_4) throw e_4.error; }
    }
    var lastSeg = segments[segments.length - 1];
    var last = (_c = lastSeg[lastSeg.length - 1]) !== null && _c !== void 0 ? _c : prev;
    var prevPt = lastSeg.length > 1 ? lastSeg[lastSeg.length - 2] : prev;
    var angle = radToDeg(Math.atan2(last.y - prevPt.y, last.x - prevPt.x));
    return { x: last.x, y: last.y, angle: angle };
}
/** Uniformly sample path into N points for morphing. */
export function samplePath(d, samples) {
    var total = getPathLength(d);
    if (total === 0 || samples < 2) {
        var flat = flattenSegments(parsePathSegments(d));
        return flat.length > 0 ? flat : [{ x: 0, y: 0 }];
    }
    var pts = [];
    for (var i = 0; i < samples; i++) {
        var pt = getPointAtLength(d, (i / (samples - 1)) * total);
        pts.push({ x: pt.x, y: pt.y });
    }
    return pts;
}
/** Interpolate between two path `d` strings (resampled to equal point count). */
export function morphPath(from, to, t, samples) {
    if (samples === void 0) { samples = 32; }
    var fromPts = samplePath(from, samples);
    var toPts = samplePath(to, samples);
    var pts = [];
    for (var i = 0; i < samples; i++) {
        pts.push({
            x: lerp(fromPts[i].x, toPts[i].x, t),
            y: lerp(fromPts[i].y, toPts[i].y, t),
        });
    }
    if (pts.length === 0)
        return from;
    var d = "M".concat(pts[0].x, " ").concat(pts[0].y);
    for (var i = 1; i < pts.length; i++) {
        d += " L".concat(pts[i].x, " ").concat(pts[i].y);
    }
    return d;
}
/** Rect outline as closed path (for morph demos). */
export function rectPath(width, height, cornerRadius) {
    if (cornerRadius === void 0) { cornerRadius = 0; }
    var r = Math.min(cornerRadius, width / 2, height / 2);
    if (r <= 0) {
        return "M0 0 L".concat(width, " 0 L").concat(width, " ").concat(height, " L0 ").concat(height, " Z");
    }
    return [
        "M".concat(r, " 0"),
        "L".concat(width - r, " 0"),
        "Q".concat(width, " 0 ").concat(width, " ").concat(r),
        "L".concat(width, " ").concat(height - r),
        "Q".concat(width, " ").concat(height, " ").concat(width - r, " ").concat(height),
        "L".concat(r, " ").concat(height),
        "Q0 ".concat(height, " 0 ").concat(height - r),
        "L0 ".concat(r),
        "Q0 0 ".concat(r, " 0"),
        'Z',
    ].join(' ');
}
